/**
 * Follow-ups — a legacy queue row → what the redesigned table's cells draw (redesign Phase 0).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). The two rules it needs from
 * its siblings are INJECTED, the way scheduleView/checklist.js takes `describe`:
 *   ctx.fu          resolveFollowupState(row, …) — already resolved by the caller
 *   ctx.categoryOf  statusCategory (label, { teamioFollowupStatusID }) → category
 *   ctx.reminders   reminderHint.reminderCell(row, …) — recall only, when reminders are paid for
 *
 * ── FIELD NAMES ────────────────────────────────────────────────────────────────────────────────
 * Every field read below is one AdminTable.vue's cell templates read (`props.row.X`), checked
 * against the main-service SQL that produces the rows. A field the legacy table does not read is
 * not invented here.
 *
 * ── NAMES ARE ALREADY MASKED ───────────────────────────────────────────────────────────────────
 * With the demo mask on, the axios interceptor has already replaced Patient / Guarantor with a
 * fake "Last, First". Initials are derived from THAT string, so they always match the name shown.
 * Never mask here (CLAUDE.md "Demo mask").
 *
 * ── FORMATTING ─────────────────────────────────────────────────────────────────────────────────
 * Dates: the `dateformat` filter with the site's DateFormat (mixin dateFormatStr, default
 * MM/DD/YYYY), done by hand on the 'YYYY-MM-DD' strings the rows carry. Money: the `money` filter,
 * with two deliberate fixes, both visible in the tests — a zero uses the site's symbol (legacy
 * hard-codes '$0.00'), and a negative reads '-$120.00' (legacy '$-120.00', the design '-$120.00').
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[ T])/;

function ymdOf(value) {
  if (typeof value !== 'string') return null;
  const m = YMD_RE.exec(value);
  if (!m) return null;
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** The `dateformat` filter for 'YYYY-MM-DD…' strings: YYYY / YY / MM / M / DD / D tokens. */
export function formatDate(value, format) {
  const ymd = ymdOf(value);
  if (!ymd) return '';
  const fmt = format || 'MM/DD/YYYY';
  const y = ymd.slice(0, 4);
  const m = ymd.slice(5, 7);
  const d = ymd.slice(8, 10);
  return fmt.replace(/YYYY|YY|MM|M|DD|D/g, (t) => {
    if (t === 'YYYY') return y;
    if (t === 'YY') return y.slice(2);
    if (t === 'MM') return m;
    if (t === 'M') return String(Number(m));
    if (t === 'DD') return d;
    return String(Number(d));
  });
}

/** The `money` filter: '' for blank, symbol + 2 decimals with thousands commas. See the header. */
export function formatMoney(value, symbol) {
  const sym = symbol || '$';
  if (value === null || value === undefined || value === '') return '';
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '';
  const body = Math.abs(n).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  return (n < 0 ? '-' : '') + sym + body;
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Whole days from `fromYmd` to `toYmd` (positive when `toYmd` is later); null if either is unreadable. */
export function daysBetween(fromYmd, toYmd) {
  const a = ymdOf(fromYmd);
  const b = ymdOf(toYmd);
  if (!a || !b) return null;
  const t = s => Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  return Math.round((t(b) - t(a)) / 86400000);
}

/** The export's relative label: today / yesterday / N days ago / N months ago. */
export function agoText(value, today) {
  const n = daysBetween(value, today);
  if (n === null) return '';
  if (n <= 0) return 'today';
  if (n === 1) return 'yesterday';
  if (n < 45) return `${n} days ago`;
  return `${Math.round(n / 30.4)} months ago`;
}

function firstLetter(s) {
  const str = String(s || '').trim();
  for (let i = 0; i < str.length; i += 1) {
    const ch = str[i];
    if (ch.toUpperCase() !== ch.toLowerCase()) return ch.toUpperCase();
  }
  return '';
}

/** Avatar initials from the (already-masked) "Last, First" — First's letter, then Last's. */
export function initialsOf(name) {
  const parts = String(name || '').split(',');
  if (parts.length < 2) return firstLetter(parts[0]);
  return firstLetter(parts.slice(1).join(',')) + firstLetter(parts[0]);
}

/** "Last, First" → "Last" (the export's "Dr. Last"). */
function lastNameOf(name) {
  return String(name || '').split(',')[0].trim();
}

const ATTEMPT_KIND = { P: 'call', E: 'email' };

/**
 * The Attempts cell. `has` is legacy's `v-if="props.row.Attempts"` (truthiness of the counter);
 * the icons are the first three letters of AttemptDetails ('P' call, 'E' email, anything else a
 * text — as the legacy `v-else` mobile icon), then "+N".
 */
export function attemptsOf(row) {
  const r = row || {};
  const has = !!r.Attempts;
  const letters = has && r.AttemptDetails ? String(r.AttemptDetails).split(',') : [];
  const kinds = letters.map(l => ATTEMPT_KIND[l] || 'text');
  const count = toNumber(r.Attempts) || 0;
  const tally = { call: 0, text: 0, email: 0 };
  kinds.forEach((k) => { tally[k] += 1; });
  const parts = [
    tally.call ? `${tally.call} call${tally.call > 1 ? 's' : ''}` : '',
    tally.text ? `${tally.text} text${tally.text > 1 ? 's' : ''}` : '',
    tally.email ? `${tally.email} email${tally.email > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(', ');
  return {
    has,
    count,
    kinds,
    icons: kinds.slice(0, 3),
    more: kinds.length > 3 ? `+${kinds.length - 3}` : '',
    tip: has
      ? `${count} attempt${count === 1 ? '' : 's'}${parts ? ` · ${parts}` : ''}`
      : 'No attempts made for this patient yet',
  };
}

/**
 * The Status cell's label. Unscheduled treatment with a NextVisit reads "Apt on Schedule" (legacy
 * Status slot). ⚠ Legacy tests `NextVisit !== null`, so a row with NO NextVisit key would read
 * "Apt on Schedule" with a blank date; here `undefined` counts as no visit (deliberate).
 */
export function statusLabelOf(queue, row) {
  const r = row || {};
  if (queue && queue.key === 'unscheduledTreatment' && r.NextVisit !== null && r.NextVisit !== undefined) {
    return 'Apt on Schedule';
  }
  return r.Status === null || r.Status === undefined ? '' : String(r.Status);
}

const CLAIM_TYPE_LABEL = { P: 'Primary', S: 'Secondary', PreAuth: 'Pre-auth' };

/**
 * One row → its view-model.
 * @param {Object} queue   an entry of queues.js QUEUES
 * @param {Object} row     the legacy row
 * @param {Object} ctx     { siteID, today, dateFormat, currency, fu, categoryOf, reminders }
 */
export function buildRow(queue, row, ctx) {
  const q = queue || {};
  const r = row || {};
  const c = ctx || {};
  const date = v => formatDate(v, c.dateFormat);
  const money = v => formatMoney(v, c.currency);
  const isRecall = q.key === 'overdueRecall';

  const name = r[q.nameField || 'Patient'] === null || r[q.nameField || 'Patient'] === undefined
    ? '' : String(r[q.nameField || 'Patient']);
  const ageNum = toNumber(r.Age);
  const subParts = [];
  if (q.nameField === 'Guarantor') subParts.push('Guarantor');
  if (isRecall && ageNum !== null) subParts.push(`${ageNum} yrs`);
  if (r.Provider) subParts.push(`Dr. ${lastNameOf(r.Provider)}`);

  const carrier = r.Carrier !== null && r.Carrier !== undefined && r.Carrier !== '' ? String(r.Carrier)
    : (r.Insurance ? String(r.Insurance) : '');
  const statusLabel = statusLabelOf(q, r);
  const attempts = attemptsOf(r);
  const sentDays = daysBetween(r.DateSent, c.today);
  const sentType = r.ClaimType ? (CLAIM_TYPE_LABEL[r.ClaimType] || String(r.ClaimType)) : '';
  const sentSub = sentDays === null ? '' : [sentType, `${sentDays} days`].filter(Boolean).join(' · ');
  const amount = toNumber(r.PatientAmount);

  const cells = {
    check: {},
    remove: { removable: true },
    patient: { name, initials: initialsOf(name), sub: subParts.join(' · ') },
    guarantor: { name, initials: initialsOf(name), sub: subParts.join(' · ') },
    recall: { main: date(r.DateDue), sub: [r.RecallType, r.RecallInterval].filter(Boolean).map(s => String(s).trim()).join(' · ') },
    hyg: r.LastHygienist ? { main: String(r.LastHygienist) } : { muted: '—' },
    ins: carrier
      ? { main: carrier, sub: '2×/yr preventive', yes: r.PreventiveFrequency2xPerYear === 'Yes', no: r.PreventiveFrequency2xPerYear === 'No' }
      : { muted: 'No insurance' },
    utins: carrier
      ? { main: carrier, sub: r.annualMax ? `${money(r.amountRemaining)} left of ${money(r.annualMax)}` : '' }
      : { muted: 'No insurance' },
    carrier: { main: carrier },
    txdate: { main: date(r.dateTxPlanned), sub: agoText(r.dateTxPlanned, c.today) },
    txamt: { main: money(r.officeFeeTotal), sub: r.adjustedFeeTotal !== null && r.adjustedFeeTotal !== undefined ? `${money(r.adjustedFeeTotal)} adjusted` : '' },
    sent: { main: date(r.DateSent), sub: sentDays !== null && sentDays > 90 ? '' : sentSub, subDanger: sentDays !== null && sentDays > 90 ? sentSub : '' },
    service: { main: date(r.DateService) },
    received: { main: date(r.DateReceived) },
    // Legacy prints "Ins Est" only when the row has a ClaimType.
    claimfee: { main: money(r.ClaimFee), sub: r.ClaimType ? `Ins est ${money(r.InsuranceEstimate)}` : '' },
    balance: { main: money(r.PatientAmount), sub: amount !== null && amount < 0 ? 'Credit' : '' },
    lastvisit: { main: date(r.LastVisit), sub: agoText(r.LastVisit, c.today) },
    billing: { main: r.BillingType ? String(r.BillingType) : '' },
    brokendate: { main: date(r.Date), sub: agoText(r.Date, c.today) },
    proc: { code: r.AdjustmentProcedureCode ? String(r.AdjustmentProcedureCode) : '', desc: r.BrokenAppointmentProcDescript ? String(r.BrokenAppointmentProcDescript) : '' },
    fee: { main: money(r.Fee) },
    status: {
      label: statusLabel,
      sub: statusLabel === 'Apt on Schedule' ? date(r.NextVisit) : '',
      category: typeof c.categoryOf === 'function'
        ? c.categoryOf(statusLabel, { teamioFollowupStatusID: r.TeamioFollowupStatusID })
        : null,
    },
    att: attempts,
    rem: c.reminders || null,
    fu: c.fu || null,
  };

  const searchText = [name, carrier, r.LastHygienist, r.hygienist, statusLabel, r.AdjustmentProcedureCode,
    r.BrokenAppointmentProcDescript, r.BillingType, r.Provider]
    .filter(v => v !== null && v !== undefined && v !== '')
    .join(' ')
    .toLowerCase();

  const num = v => { const n = toNumber(v); return n === null ? -Infinity : n; };
  const sort = {
    patient: name.toLowerCase(),
    guarantor: name.toLowerCase(),
    recall: ymdOf(r.DateDue) || '',
    hyg: r.LastHygienist ? String(r.LastHygienist).toLowerCase() : '~',
    ins: carrier ? carrier.toLowerCase() : '~',
    utins: carrier ? carrier.toLowerCase() : '~',
    carrier: carrier ? carrier.toLowerCase() : '~',
    rem: num(r.AutoRemindersSent),
    status: statusLabel.toLowerCase(),
    att: attempts.count,
    txdate: ymdOf(r.dateTxPlanned) || '',
    txamt: num(r.officeFeeTotal),
    sent: ymdOf(r.DateSent) || '',
    service: ymdOf(r.DateService) || '',
    received: ymdOf(r.DateReceived) || '',
    claimfee: num(r.ClaimFee),
    balance: num(r.PatientAmount),
    lastvisit: ymdOf(r.LastVisit) || '',
    billing: r.BillingType ? String(r.BillingType).toLowerCase() : '~',
    brokendate: ymdOf(r.Date) || '',
    proc: String(r.AdjustmentProcedureCode || r.BrokenAppointmentProcDescript || '~').toLowerCase(),
    fee: num(r.Fee),
  };

  let id = null;
  if (q.uniqueField === 'ClaimNum') id = r.ClaimNum;
  else id = r.PatNum ? `${c.siteID}-${r.PatNum}` : r.TeamioPatientID;

  return {
    id: id === null || id === undefined || id === '' ? null : String(id),
    queueKey: q.key || null,
    row: r,
    name,
    initials: cells.patient.initials,
    provider: r.Provider ? String(r.Provider) : '',
    hygienist: r.hygienist ? String(r.hygienist) : '',
    age: ageNum,
    list: r.BelongsToListID ? { id: r.BelongsToListID, name: r.ListName ? String(r.ListName) : '', pageName: q.listPageName || null } : null,
    aiCallable: !!(r.WirelessPhone && r.TxtMsgOk === 1),
    statusLabel,
    attempts,
    fu: c.fu || null,
    cells,
    searchText,
    sort,
  };
}

// =================================================================================================
// PATIENT PROFILE (redesign Phase 5, 2026-10-02) — one patient's follow-ups across every queue.
//
// ── WHAT A PROFILE ROW CARRIES ───────────────────────────────────────────────────────────────────
// main-service sql/patient-profile.js `upcomingFollowups` (result set 6) selects teamio.tasks
// fields ONLY: TaskID, TaskTypeID, TaskTypeDisplayName, FollowupDate, AssignedToEmpID, Overridden,
// InitialFollowupNote, InitialFollowupCreatedOn, CreatedAutomatically, InitialFollowupDateSet,
// FollowupStopped, EmployeeManuallyAssigned, EmployeeName, ClaimNum, PendingUnscheduledTx,
// Attempts, AttemptDetails, FollowupStatusID, TeamioFollowupStatusID, Status (never null — the SQL
// IFNULLs it to 'Not Called'), completed, dueToday, pastDue. No PatNum, no name, no recall due
// date, no claim sent date / carrier / fee, no balance, no broken date. The queue pages' cells
// cannot be built from it. The functions below call a profile row `task` (never `r`): its fields
// are pinned against that SQL by cypress/node-tests/followups-patient-profile-rules.test.js, the
// queue rows' `r.X` against AdminTable by followups-row-model.test.js.
//
// ── THE DETAILS COLUMN ───────────────────────────────────────────────────────────────────────────
// So the one-line facts come from (a) the row itself (ClaimNum, InitialFollowupCreatedOn) and
// (b) `facts` — what the PAGE has already loaded for this patient (other result sets of the same
// PATIENT_PROFILE query), turned into plain values by profileFactsFrom(). No new query. A host
// that has none (the Schedule View Patient Panel, later) passes nothing and gets the row facts.
// =================================================================================================

/** The export's QICON, as FuIcon names ('tx' is the list glyph, drawn as `list`). */
export const QUEUE_ICONS = {
  overdueRecall: 'recall',
  unscheduledTreatment: 'list',
  outstandingClaims: 'receipt',
  outstandingPreAuths: 'note',
  processedPreAuths: 'note',
  patientBalances: 'dollar',
  brokenAppointments: 'calx',
};

/**
 * The patient facts the Details column can use, from result sets Patient Profile already holds:
 *   patient                 result set 1 row: RecallScheduled, PatientAmount, PatNum, Guarantor
 *   unscheduledTreatments   result set 3: DateTP per unscheduled procedure
 *   brokenAppointments      result set 4: Date, AdjustProcCode per broken appointment / adjustment
 * The balance is the family's (OD keeps BalTotal / InsEst on the GUARANTOR only, so another
 * family member's PatientAmount reads 0) — shown only when this patient IS the guarantor.
 */
export function profileFactsFrom({ patient, unscheduledTreatments, brokenAppointments } = {}) {
  const p = patient || null;
  // OD writes 0001-01-01 / 1901-01-01 for "no date" — not a fact.
  const realYmd = (v) => {
    const d = ymdOf(v);
    return d && d >= '1902-01-01' ? d : null;
  };
  const latest = (list, field) => (Array.isArray(list) ? list : [])
    .reduce((best, x) => {
      const d = realYmd(x && x[field]);
      return d && (!best || d > best.date) ? { date: d, row: x } : best;
    }, null);

  const tx = latest(unscheduledTreatments, 'DateTP');
  const broken = latest(brokenAppointments, 'Date');
  const isGuarantor = !!(p && p.PatNum !== null && p.PatNum !== undefined && String(p.PatNum) === String(p.Guarantor));

  return {
    recallScheduled: p ? realYmd(p.RecallScheduled) : null,
    balance: isGuarantor ? toNumber(p.PatientAmount) : null,
    txPlanned: tx ? { date: tx.date, count: unscheduledTreatments.length } : null,
    lastBroken: broken ? { date: broken.date, code: broken.row.AdjustProcCode ? String(broken.row.AdjustProcCode) : '' } : null,
  };
}

/**
 * The Details cell for one profile row: { main, sub } (or { muted } when there is nothing).
 * Queue facts first (from `ctx.facts`), then the row's own: "Started <date>" (the day the first
 * follow-up was set — InitialFollowupCreatedOn) and the claim / pre-auth number.
 */
export function profileDetailOf(queue, row, ctx) {
  const q = queue || {};
  const task = row || {};
  const c = ctx || {};
  const f = c.facts || {};
  const date = v => formatDate(v, c.dateFormat);
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

  let main = '';
  let sub = '';
  switch (q.key) {
    case 'overdueRecall':
      if (f.recallScheduled) main = `Recall appointment ${date(f.recallScheduled)}`;
      break;
    case 'unscheduledTreatment':
      if (f.txPlanned) {
        main = `Tx planned ${date(f.txPlanned.date)}`;
        sub = `${plural(f.txPlanned.count, 'procedure')} not scheduled`;
      }
      break;
    case 'outstandingClaims':
      if (task.ClaimNum) main = `Claim #${task.ClaimNum}`;
      break;
    case 'outstandingPreAuths':
    case 'processedPreAuths':
      if (task.ClaimNum) main = `Pre-auth #${task.ClaimNum}`;
      break;
    case 'patientBalances':
      if (f.balance !== null && f.balance !== undefined) {
        main = `Balance ${formatMoney(f.balance, c.currency)}`;
        if (f.balance < 0) sub = 'Credit';
      }
      break;
    case 'brokenAppointments':
      if (f.lastBroken) {
        main = `Broken ${date(f.lastBroken.date)}`;
        sub = f.lastBroken.code;
      }
      break;
    default:
      break;
  }

  const started = ymdOf(task.InitialFollowupCreatedOn) ? `Started ${date(task.InitialFollowupCreatedOn)}` : '';
  if (!main) return started ? { main: started, sub: '' } : { muted: '—' };
  return { main, sub: sub || started };
}

/** Patient Profile's Status label: `ufp.Status || 'Not Called'` (no "Apt on Schedule" swap). */
export function profileStatusLabelOf(row) {
  const s = row ? row.Status : null;
  return s === null || s === undefined || s === '' ? 'Not Called' : String(s);
}

/**
 * One Patient Profile row → its view-model. `queue` is the ROW'S queue (queueByTaskTypeID), so
 * the follow-up rules, the popover and the saves all use that queue. Builds on buildRow and
 * replaces what differs on the profile:
 *   id        'task-<TaskID>' (queues.js profileRowIdOf — the rows have no PatNum)
 *   name      the patient's (ctx.patientName) — every row is the same patient
 *   status    profileStatusLabelOf
 *   att       icons only when Attempts AND AttemptDetails (Patient Profile's `v-if`)
 *   queue     icon + the queue's label; detail  profileDetailOf
 * @param {Object} ctx  buildRow's ctx + { patientName, facts }
 */
export function buildProfileRow(queue, row, ctx) {
  const q = queue || {};
  const task = row || {};
  const c = ctx || {};
  const vm = buildRow(q, task, c);
  const name = c.patientName ? String(c.patientName) : '';
  const statusLabel = profileStatusLabelOf(task);
  const attempts = task.AttemptDetails
    ? vm.attempts
    : Object.assign({}, vm.attempts, { has: false, kinds: [], icons: [], more: '', tip: 'No attempts made for this patient yet' });
  const label = q.label || (task.TaskTypeDisplayName ? String(task.TaskTypeDisplayName) : 'Follow-up');
  const detail = profileDetailOf(q, task, c);
  const id = task.TaskID === null || task.TaskID === undefined || task.TaskID === '' ? null : `task-${task.TaskID}`;

  const cells = Object.assign({}, vm.cells, {
    patient: { name, initials: initialsOf(name), sub: '' },
    status: {
      label: statusLabel,
      sub: '',
      category: typeof c.categoryOf === 'function'
        ? c.categoryOf(statusLabel, { teamioFollowupStatusID: task.TeamioFollowupStatusID })
        : null,
    },
    att: attempts,
    queue: { key: q.key || null, label, icon: QUEUE_ICONS[q.key] || 'list' },
    detail,
  });

  return Object.assign(vm, {
    id,
    queueKey: q.key || null,
    name,
    initials: initialsOf(name),
    list: null,
    aiCallable: false,
    statusLabel,
    attempts,
    cells,
    searchText: [label, detail.main, detail.sub, statusLabel].filter(Boolean).join(' ').toLowerCase(),
    sort: Object.assign({}, vm.sort, { queue: label.toLowerCase(), status: statusLabel.toLowerCase(), att: attempts.count }),
  });
}

// =================================================================================================
// ADMIN "TODAY" WORKLIST (Admin redesign Phase A1, 2026-10-06) — the merged worklist on /app/admin.
//
// Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Today 3a", "Phase A — A1".
//
// ── WHAT A DASHBOARD ROW CARRIES ─────────────────────────────────────────────────────────────────
// main-service sql/admin-dashboard.js: every queue selects `tt.*` (teamio.tasks — TaskID, PatNum,
// TeamioPatientID, FollowupDate, Attempts, AttemptDetails, ClaimNum, AssignedToEmpID,
// BrokenAppointmentProcDescript, InitialFollowupCreatedOn, …) plus Patient ("Last, First"), Status,
// completed / dueToday / pastDue; claims and pre-auths add Carrier + DateSent; balances add
// PatientAmount. NOT on these rows: DateDue / RecallType / RecallInterval / AutoRemindersSent,
// officeFeeTotal / dateTxPlanned, Date / AdjustmentProcedureCode, ClaimFee, LastVisit, Guarantor.
// dashboardDetailOf reads the SAME field names the queue pages use (buildRow above), so a fact
// shows the day the query selects it — and until then that fragment is simply left out.
// =================================================================================================

/** The design's short type labels (the TYPE chip, and the done rail's "Balance · Texted"). */
export const DASHBOARD_TYPE_LABELS = {
  overdueRecall: 'Recall',
  unscheduledTreatment: 'Unscheduled tx',
  brokenAppointments: 'Broken appt',
  outstandingClaims: 'Claim',
  patientBalances: 'Balance',
  outstandingPreAuths: 'Pre-auth',
  processedPreAuths: 'Pre-auth',
};

function present(v) {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

/**
 * The DETAILS cell of a worklist row: { main, sub, money } — or { muted: '—' } when the row has
 * nothing to say. Per queue (the design's lines; a missing field drops only its own fragment):
 *   Recall       DateDue                 / RecallType · RecallInterval · N auto reminders
 *   Unscheduled  officeFeeTotal (money)  / Planned dateTxPlanned
 *   Broken       Date                    / AdjustmentProcedureCode · BrokenAppointmentProcDescript
 *   Claim        Carrier                 / Sent DateSent · ClaimFee
 *   Balance      PatientAmount (money)   / Last visit LastVisit
 *   Pre-auth     Carrier                 / Sent DateSent
 * With no main line the sub line moves up; with neither, the row's own "Started <date>" (the day
 * its first follow-up was set — InitialFollowupCreatedOn), as on Patient Profile.
 */
export function dashboardDetailOf(queue, row, ctx) {
  const q = queue || {};
  const r = row || {};
  const c = ctx || {};
  const date = v => formatDate(v, c.dateFormat);
  const money = v => formatMoney(v, c.currency);
  const join = parts => parts.filter(Boolean).join(' · ');

  let main = '';
  let sub = '';
  let isMoney = false;
  switch (q.key) {
    case 'overdueRecall': {
      main = date(r.DateDue);
      const n = toNumber(r.AutoRemindersSent);
      let reminders = '';
      if (n !== null) reminders = n > 0 ? `${n} auto reminder${n === 1 ? '' : 's'}` : 'no auto reminders';
      sub = join([present(r.RecallType) ? String(r.RecallType).trim() : '', present(r.RecallInterval) ? String(r.RecallInterval).trim() : '', reminders]);
      break;
    }
    case 'unscheduledTreatment':
      main = money(r.officeFeeTotal);
      isMoney = !!main;
      sub = date(r.dateTxPlanned) ? `Planned ${date(r.dateTxPlanned)}` : '';
      break;
    case 'brokenAppointments':
      main = date(r.Date);
      sub = join([present(r.AdjustmentProcedureCode) ? String(r.AdjustmentProcedureCode).trim() : '',
        present(r.BrokenAppointmentProcDescript) ? String(r.BrokenAppointmentProcDescript).trim() : '']);
      break;
    case 'outstandingClaims':
      main = present(r.Carrier) ? String(r.Carrier).trim() : '';
      sub = join([date(r.DateSent) ? `Sent ${date(r.DateSent)}` : '', money(r.ClaimFee)]);
      break;
    case 'outstandingPreAuths':
    case 'processedPreAuths':
      main = present(r.Carrier) ? String(r.Carrier).trim() : '';
      sub = date(r.DateSent) ? `Sent ${date(r.DateSent)}` : '';
      break;
    case 'patientBalances':
      main = money(r.PatientAmount);
      isMoney = !!main;
      sub = date(r.LastVisit) ? `Last visit ${date(r.LastVisit)}` : '';
      break;
    default:
      break;
  }

  if (!main && sub) {
    main = sub;
    sub = '';
  }
  if (!main) {
    // A teamio.tasks column (the dashboard selects tt.*), not an AdminTable field — so it is read
    // as `task.X`, like the profile rows above (followups-row-model.test.js pins every `r.X`).
    const task = r;
    const started = ymdOf(task.InitialFollowupCreatedOn) ? `Started ${date(task.InitialFollowupCreatedOn)}` : '';
    return started ? { main: started, sub: '', money: false } : { muted: '—' };
  }
  return { main, sub, money: isMoney };
}

/**
 * One ADMIN_DASHBOARD row → its worklist view-model. `queue` is the ROW'S queue (queues.js QUEUES
 * entry for the item's queueKey), so the follow-up rules, the popover and the saves use it.
 * Builds on buildRow and replaces what differs on the worklist:
 *   id        '<queueKey>:task-<TaskID>' (one patient can be in several queues, and twice in one);
 *             `actionId` = buildRow's id — what followupActions.js keys its saving / opening state by
 *   name      the row's Patient (balances rows carry Patient, not Guarantor), else the queue's field
 *   status    statusLabelOf; a row with no Status reads 'Not Called' (the same tasks row on Patient
 *             Profile does — its SQL IFNULLs it)
 *   queue     { key, label: DASHBOARD_TYPE_LABELS, icon }; detail  dashboardDetailOf
 * @param {Object} ctx  buildRow's ctx: { siteID, today, dateFormat, currency, fu, categoryOf }
 */
export function buildDashboardRow(queue, row, ctx) {
  const q = queue || {};
  const r = row || {};
  const c = ctx || {};
  const vm = buildRow(q, r, Object.assign({}, c, { reminders: null }));
  const rawName = present(r.Patient) ? r.Patient : r[q.nameField || 'Patient'];
  const name = present(rawName) ? String(rawName) : '';
  const label = statusLabelOf(q, r) || 'Not Called';
  const detail = dashboardDetailOf(q, r, c);
  const typeLabel = DASHBOARD_TYPE_LABELS[q.key] || q.label || 'Follow-up';
  const own = present(r.TaskID) ? `task-${r.TaskID}` : vm.id;
  const id = own ? `${q.key || 'queue'}:${own}` : null;
  const carrier = present(r.Carrier) ? String(r.Carrier) : '';

  const cells = Object.assign({}, vm.cells, {
    patient: { name, initials: initialsOf(name), sub: '' },
    status: {
      label,
      sub: vm.cells.status.sub,
      category: typeof c.categoryOf === 'function'
        ? c.categoryOf(label, { teamioFollowupStatusID: r.TeamioFollowupStatusID })
        : null,
    },
    queue: { key: q.key || null, label: typeLabel, icon: QUEUE_ICONS[q.key] || 'list' },
    detail,
  });

  return Object.assign(vm, {
    id,
    actionId: vm.id,
    queueKey: q.key || null,
    name,
    initials: initialsOf(name),
    statusLabel: label,
    cells,
    searchText: [name, carrier].filter(Boolean).join(' ').toLowerCase(),
    sort: Object.assign({}, vm.sort, { patient: name.toLowerCase(), queue: typeLabel.toLowerCase(), status: label.toLowerCase() }),
  });
}
